<template lang="pug">
.feedback-listing
  template(v-if="isFetching")
    v-row(no-gutters)
      v-col(cols="8")
        v-skeleton-loader(type="article@3")
  template(v-else)
    template(v-if="totalCount === 0")
      listing-empty-state.my-4(v-if="hasFiltersOrSearch" iconName="$yzi-star-01" :primaryText="$t('item_not_found', { item:'invitation' })" :secondaryText="$t('item_not_found_description')")
      listing-empty-state.my-4(v-else iconName="$yzi-users-02" :primaryText="$t('invitation_listing_empty_primary_text')" :secondaryText="$t('invitation_listing_empty_secondary_text', { text: listingEmptyText })")
    template(v-else)
      .data-table
        v-data-table.mt-sm-4.pb-6(:headers="headers" :items="invitations" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" item-class="text-sm" :disable-sort="isMobileScreen" :header-props="{ sortIcon: '$yzi-triangle' }" :mobile-breakpoint="0")
          template(#header.checked)
            yc-checkbox(v-model="checkAll" @change="onCheckedAll")
          template(v-slot:item="{ item, isMobile, headers, index }")
            tr.primary--text
              template(v-for="header in headers")
                template(v-if="header.value === 'checked'")
                  td.py-4.sticky-column
                    yc-checkbox(v-model="item.checked" @change="onChecked")
                template(v-else-if="header.text.toLowerCase() === 'name'")
                  td.py-4.sticky-column
                    .font-medium {{ item.lead.name }}
                template(v-else-if="header.value === 'actions'")
                  td.py-4
                    div.d-flex.align-center.justify-end
                      v-btn.button-xs(icon color="neutral700" :disabled="isDisabled('whatsapp',item)" @click="clickWhatsapp({bulk:false, singleItem:item})")
                        v-icon(size="20") mdi-whatsapp
                      v-btn.button-xs(icon color="neutral700" :disabled="isDisabled('email',item)" @click="clickEmail({bulk:false, singleItem:item})")
                        v-icon(size="20") $yzi-mail-01
                      v-btn.button-xs(icon color="neutral700" :disabled="isDisabled('sms',item)" @click="clickSms({bulk:false, singleItem:item})")
                        v-icon(size="20") $yzi-message-square-02
                template(v-else-if="header.value === 'created' && item.created")
                  td.py-4 {{ convertDateTime(item.created) }}
                template(v-else)
                  td.py-4 {{ item[header.text.toLowerCase()] }}
        div.d-flex.align-center
          v-spacer
          div
            yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" text="Item list" @input="pageChanged")

  .dialogs
    send-invitation-dialog(v-if="openSendReviewInvitateDialog" :open.sync="openSendReviewInvitateDialog" :sendingMethod="sendingMethod" :previewMessage="previewMessage" :emailSubject="emailSubject" :isLoading="isSending" @send="sendInvitation" @close="resetInvitationData")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ListingEmptyState from '@/components/review/listing/ListingEmptyState.vue'
import SendInvitationDialog from '@/components/review/dialogs/SendInvitationDialog.vue'

export default {
  name: 'InvitationListing',
  components: {
    ListingEmptyState,
    SendInvitationDialog
  },
  props: {
    searchText: {
      type: String,
      default: ''
    },
    filters: {
      type: Array,
      default: () => {
        return []
      }
    },
    os: {
      type: String,
      default: 'review',
      validator(value) {
        return ['clinic', 'donation', 'connect', 'review'].includes(value)
      }
    },
  },
  data() {
    return {
      isFetching: false,
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      checkAll: false,
      selectedInvitations: [],
      invitations: [],
      reviewSetting: null,
      isSending: false,
      sendingSingleInvitation: false,
      openSendReviewInvitateDialog: false,
      sendingMethod: null,
      previewMessage: null, // For whatsapp and sms
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      reviewInvitationFields: 'review/getInvitationFields',
    }),
    isMobileScreen() {
      return this.$vuetify.breakpoint.xs
    },
    hasFiltersOrSearch() {
      return this.filters.length > 0 || this.searchText.length > 0
    },
    headers() {
      const checkbox = {
        text: '',
        value: 'checked',
        sortable: false
      }
      const name = {
        text: 'Name',
        value: 'lead.name',
        enable: true,
        class: 'text-xs font-bold neutral500--text text-uppercase'
      }
      const fields = this.reviewInvitationFields.filter(item => item.enable)
      fields.map(item => {
        item.text = item.field_name
        item.value = item.id
        item['class'] = 'text-xs font-bold neutral500--text text-uppercase'
      })
      const actions = {
        text: 'Send invite via..',
        value: 'actions',
        enable: true,
        class: 'text-xs font-bold text-end neutral500--text text-uppercase',
        sortable: false
      }
      return [checkbox, name, ...fields, actions]
    },
    defaultMessageTemplate() {
      return this.reviewSetting.review_sms_template
    },
    appointmentLink() {
      return `https://${this.company.slug}.yezza.co/appointment`
    },
    reviewLink() {
      if (process.env.VUE_APP_ENV === 'prod') {
        return `https://review.yezza.co/review`
      }
      return `https://review-test.yezza.co/review`
    },
    emailSubject() {
      return this.reviewSetting.email_subject
    },
    canBulkEmail() {
      return this.selectedInvitations.length > 0 && this.selectedInvitations.every(selected => selected.lead.email)
    },
    canBulkSms() {
      return this.selectedInvitations.length > 0 && this.selectedInvitations.every(selected => selected.lead.phone)
    },
    listingEmptyText() {
      switch (this.os) {
        case 'clinic':
          return this.$t('invitation_listing_empty_clinic_text')
      
        default:
          return this.$t('invitation_listing_empty_review_text')
      }
    }
  },
  watch: {
    pageSize() {
      this.fetchInvitation()
    },
    filters() {
      this.fetchInvitation()
    },
    selectedInvitations(newValue) {
      if (newValue.length > 0 && !this.sendingSingleInvitation) {
        this.$emit('selected')
        return
      }
      this.$emit('clear')
    }
  },
  async mounted() {
    try {
      this.isFetching = true
      const allFetching = [this.fetchInvitation(), this.fetchReviewSetting()]
      await Promise.all([...allFetching])
    } catch (error) {
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
    } finally {
      this.isFetching = false
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    pageChanged(num) {
      this.fetchInvitation(num)
    },
    onCheckedAll() {
      const clone = [...this.invitations]
      clone.map(item => item.checked = this.checkAll)
      this.invitations = [...clone]
      this.selectedInvitations = this.invitations.filter(item => item.checked === true)
    },
    clearSelection() {
      this.invitations.forEach(item => item.checked = false)
      this.checkAll = false
      this.selectedInvitations = []
    },
    onChecked () {
      this.selectedInvitations = this.invitations.filter(item => item.checked === true)
      this.invitations = [...this.invitations]
      this.checkAll = false
    },
    formatInput(string) {
      string = String(string);
      
      //-----> for bold text
      let bold = string.split(" ").map((word) =>
      {
        if (/\*/.test(word) && !/^\**$/.test(word))
          if (/(^(~\*)|^(_\*)|^\*)(.*)(\*$|(\*_)$|(\*\W)$)/.test(word))
            return word.replace(/\*([^\r\n]+)\*/g, "<b>$1</b>")
          else if (/[_*~]\*(.*)\*[~*_]/.test(word))
            return word.replace(/\*([^*][^\r\n]+[^*])\*/g, "<b>$1</b>");
        return word;
      }).join(" ");

      //-----> for italic text 
      let italic = bold.split(" ").map((word) =>
      {
        if (/_/.test(word) && !/^_+$/.test(word))
        if (/(^~_|^_<.*>|^_\*|^_)(.*)(_$|(_<.*>)$|(_\*)$|(_\W)$)/.test(word))
          return word.replace(/_([^\r\n]+)_/g, "<i>$1</i>");
        else if (/(~|_|\*|<.*>)_(.*)_(<.*>|\*|~|_)/.test(word))
          return word.replace(/_([^_][^\r\n]+[^_])_/g, "<i>$1</i>");
        return word;
      }).join(" ");
      
      //----->for strikethrough/deleted text 
      let strikethrough = italic.split(" ").map((word) =>
      {
        if (/~/.test(word) && !/^~+$/.test(word))
          if (/(^_~|^~<.*>|^\*~|^~)(.*)(~$|(~_)$|(~<.*>)$|(~\*)$|(~\W)$)/.test(word))
            return word.replace(/~([^\r\n]+)~/g, "<del>$1</del>")
          else if (/(~|_|\*|<.*>)~(.*)~(<.*>|\*|~|_)/.test(word))
            return word.replace(/~([^~][^\r\n]+[^~])~/g, "<del>$1</del>");
        return word;
      }).join(" ");
      
      
      
      //-----> for underlined text
      var underline = strikethrough.split(" ").map(function(word)
      {
        if (/%/.test(word) && !/^%+$/.test(word))
          if (/(^_%|^~%|^%<.*>|^\*%|^%)(.*)(%$|(%~)$|(%_)$|(%<.*>)$|(%\*)$|%\W+$)/.test(word))
            return word.replace(/%([^\r\n]+)%/g, "<u>$1</u>")
          else if (/(%|~|_|\*|<.*>)%(.*)%(<.*>|\*|~|%|_)/.test(word))
            return word.replace(/%([^%][^\r\n]+[^%])%/g, "<u>$1</u>");
        return word;
      }).join(" ");
      
      
      
      //-----> for monospace text
      var monospace = underline.split(" ").map(function(word)
      {
        if (/```/.test(word))
          if (/```(.|\n|\r)*(.+)(.|\n|\r])*```/.test(word)) 
            return word.replace(/```((.|\n|\r)*(.*)(\r|\n|.)*)```/g, `<span style="font-family: 'Courier New', Courier, monospace">$1</span>`);
        return word;
      }).join(" ");
        
      var formatted = monospace;
      
      //-----> for bold sentence formatting
      if (/\*(.*)\*/.test(formatted))
        formatted = formatted.replace(/(^|[\s_>~%`])\*([^\s\r\n]+[^*\n\r]+[^\s\r\n]+)\*([\W_\r\n\s]|$)/g, "$1<b>$2</b>$3");


      //-----> for italic sentence formatting 
      if (/_(.*)_/.test(formatted))
        formatted = formatted.replace(/(^|[\s>~%`*])_([^\s\r\n]+[^_\n\r]+[^\s\n\r]+)_([\W_\s\r\n]|$)/g, "$1<i>$2</i>$3");
      
      
      //-----> for strikethrough sentence formatting 
      if (/~(.*)~/.test(formatted))
        formatted = formatted.replace(/(^|[\s>%`*_])~([^\s\r\n]+[^~\r\n]+[^\r\n\s]+)~([\W_\s\r\n]|$)/g, "$1<del>$2</del>$3");
      
      
      //-----> for underline sentence formatting 
      if (/%(.*)%/.test(formatted))
        formatted = formatted.replace(/(^|[\s>~`*_])%([^\s\r\n]+[^%\r\n]+[^\r\n\s]+)%([\W_\s\r\n]|$)/g, "$1<u>$2</u>$3");
    
    
      //-----> for monospace sentence formatting
      var counter = 0;
      if (/```/.test(formatted))
      {
        var i = 0;
        formatted = formatted.split(" ");
        formatted = formatted.map(function(word)
        {
          //tempArr[i+1] handles case where input is like this: ``` ``` with no characters between back ticks
          if (!/<code>/.test(word))
            if (/```/.test(word) && formatted[i+1] != "```")
            {
              i++; counter++;
              if (counter % 2 != 0) 
                return word.replace("```", "<code>");
              else
                return word.replace("```", "</code>");
            } i++; 
          return word;
        }).join(" ");

        //handles case where there are no closing back ticks
        if (counter % 2 == 1)
        {
          formatted = formatted.split(" ");
          for (i = formatted.length-1; i >= 0; i--)
            if (/<code>/.test(formatted[i]))
            {
              formatted[i] = formatted[i].replace(/<code>/, "```");
              break;
            }
          formatted = formatted.join(" ");
        }
      }
      return formatted;
    },
    processMessage({ preview }) {
      const selected = this.selectedInvitations[0]
      let message = this.defaultMessageTemplate
      if (selected?.appointment?.data) {
        message = this.reviewSetting.appointment_sms_template
      }
      // if (selected?.consultation?.data) {
      //   message = this.reviewSetting.cms_sms_template
      // }
      // Process message
      message = message.split('{{name}}').join(selected.lead.name ? this.displayData({ data:selected.lead.name, preview }) : '')
      message = message.split('{{review_link}}').join(`${this.reviewLink}/${this.company.slug}?pid=${selected.lead.pid}`)
      if (this.os === 'clinic') {
        // Consultation data
        // if (selected?.consultation?.data) {
        //   message = message.split('{{doctor}}').join(selected.consultation.data?.provider?.name ? this.displayData({ data:selected.consultation.data.provider.name, preview }) : '')
        //   message = message.split('{{date}}').join(selected.created ? this.displayData({ data:this.$options.filters.humanizeDate(selected.created), preview }) : '')
        //   message = message.split('{{time}}').join(selected.created ? this.displayData({ data:this.$options.filters.displayTime2(selected.created), preview }) : '')
        // } else {
        // }
        // Appointment data
        message = message.split('{{doctor}}').join(selected?.appointment?.data?.provider?.name ? this.displayData({ data:selected.appointment.data.provider.name, preview }) : '')
        message = message.split('{{service}}').join(selected?.appointment?.data?.service?.name ? this.displayData({ data:selected.appointment.data.service.name, preview })  : '')
        message = message.split('{{date}}').join(selected?.appointment?.data?.date ? this.displayData({ data:this.$options.filters.humanizeDate(selected.appointment.data.date), preview }) : '')
        message = message.split('{{time}}').join(selected?.appointment?.data?.time ? this.displayData({ data:this.$options.filters.displayTime2(selected.appointment.data.time), preview }) : '')
        message = message.split('{{appointment_link}}').join(this.appointmentLink)
      }
      if (this.sendingMethod === 'WhatsApp' && !preview) {
        message = message.split(/\n/g).join('%0d%0a')
        message = this.$options.filters.encodeSymbols(message)
      }
      return message
    },
    uriEncodeWrapper(data) {
      if (this.sendingMethod === 'WhatsApp') {
        return encodeURIComponent(data)
      }
      return data
    },
    displayData ({ data, preview }) {
      if (preview) return data
      
      return this.uriEncodeWrapper(data)
    },
    clickWhatsapp({ bulk, singleItem }) {
      if (!this.reviewSetting) {
        return
      }
      if (!bulk) {
        this.sendingSingleInvitation = true
        this.selectedInvitations.push(singleItem)
      }
      this.openSendReviewInvitateDialog = true
      this.sendingMethod = 'WhatsApp'
      this.previewMessage = this.formatInput(this.processMessage({preview:true}))
      this.openSendReviewInvitateDialog = true
    },
    async sendWhatsapp() {
      this.isSending = true
      const encodedMessage = this.processMessage({preview:false})
      const phone = this.selectedInvitations[0].lead.phone
      try {
        const { data } = await this.$axios.post(`/reviews/${this.company.slug}/invites/${this.selectedInvitations[0].lead.pid}/mark_sent?pid=true`)
        this.updateInvitationStatus(data)
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        return
      }
      window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${encodedMessage}`)
      this.openSendReviewInvitateDialog = false
      this.resetInvitationData()
      this.$emit('refetchStats')
    },
    clickSms({bulk, singleItem}) {
      if (!this.reviewSetting) {
        return
      }
      if (!bulk) {
        this.sendingSingleInvitation = true
        this.selectedInvitations.push(singleItem)
      }
      this.sendingMethod = 'sms'
      this.previewMessage = this.processMessage({preview:true})
      this.openSendReviewInvitateDialog = true
    },
    async sendSms() {
      this.isSending = true
      const payload = {
        leads: this.selectedInvitations.map((selected) => {
          return {
            id: selected.lead.id,
          }
        }),
      }
      try {
        await this.$axios.post(`/reviews/${this.company.slug}/invites/send_bulk_sms`, payload)
        this.showMessage(['success', 'SMS sent successfully'])
      } catch (error) {
        const errorData = error.response?.data
        if (errorData && errorData.length) {
          this.showMessage(['error', errorData[0]])
        } else {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        }
      } finally {
        this.openSendReviewInvitateDialog = false
        this.resetInvitationData()
        this.$emit('refetchStats')
      }
    },
    clickEmail({ bulk, singleItem }) {
      if (!this.reviewSetting) {
        return
      }
      if (!bulk) {
        this.sendingSingleInvitation = true
        this.selectedInvitations.push(singleItem)
      }
      this.sendingMethod = 'email'
      this.openSendReviewInvitateDialog = true
    },
    async sendEmail() {
      this.isSending = true
      const payload = {
        leads: this.selectedInvitations.map((selected) => selected.lead.id)
      }
      try {
        await this.$axios.post(`/reviews/${this.company.slug}/invites/send_bulk_email`, payload)
        this.showMessage(['success', 'Email sent successfully'])
      } catch (error) {
        const errorData = error.response?.data
        if (errorData && errorData.length) {
          this.showMessage(['error', errorData[0]])
        } else {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        }
      } finally {
        this.openSendReviewInvitateDialog = false
        this.resetInvitationData()
        this.$emit('refetchStats')
      }
    },
    resetInvitationData() {
      this.isSending = false
      this.sendingMethod = null
      this.previewMessage = null
      if (this.sendingSingleInvitation) {
        this.sendingSingleInvitation = false
        this.selectedInvitations = []
      }
    },
    sendInvitation() {
      switch (this.sendingMethod) {
        case 'sms':
          this.sendSms()
          break;

        case 'email':
          this.sendEmail()
          break;

        default:
          this.sendWhatsapp()
          break;
      }
    },
    convertDateTime(dateTime) {
      const convertedTime = this.$options.filters.convertToRelativeCalendar(dateTime)
      // Capitalize only first word
      return convertedTime.charAt(0).toUpperCase() + convertedTime.slice(1)
    },
    async fetchInvitation(page) {
      const params = {
        expand: 'lead',
        search: this.searchText,
        page_size: this.pageSize,
        page: page || 1,
      }
      if (this.filters.length > 0) {
        this.filters.map(filter => {
          if (filter.value) {
            params[filter.key] = filter.value
          }
        })
      }
      return this.$axios.get(`/reviews/${this.company.slug}/invites`, { params }).then(({data}) => {
        const results = data.results.map(result => {
          let data = {
            ...result,
            checked: false,
          }
          if (this.os === 'clinic') {
            data = {
              ...data,
              appointment: {
                fetched: false,
                success: false,
                data: null
              },
              consultation: {
                fetched: false,
                success: false,
                data: null
              }
            }
          }
          return data
        })
        this.invitations = results
        this.totalCount = data.count
        // Fetch appointment and consultation data for each invitations
        if (this.os === 'clinic') {
          this.invitations.map((invitation) => this.fetchAppointment(invitation))
          // this.invitations.map((invitation) => this.fetchConsultation(invitation))
        }
      })
    },
    async fetchReviewSetting() {
      return this.$axios.get(`/reviews/${this.company.slug}`)
        .then(({data}) => {
          this.reviewSetting = data
          this.$store.commit('review/setSettings', data)
        })
    },
    async fetchAppointment(item) {
      try {
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/appointments?lead=${item.lead.id}`)
        item.appointment.success = true
        if (data.count > 0) {
          item.appointment.data = data.results[0]
        }
      } catch {
        item.appointment.success = false
      } finally {
        item.appointment.fetched = true
      }
    },
    // async fetchConsultation(item) {
    //   try {
    //     const params = {
    //       expand: 'provider'
    //     }
    //     const { data } = await this.$axios.get(`/clinics/${this.company.slug}/consultations/${item.lead.pid}`, { params })
    //     item.consultation.success = true
    //     item.consultation.data = data
    //   } catch(error) {
    //     const { config, status } = error.response
    //     const consultationEndpoint = config.url.includes('/consultations')
    //     if (consultationEndpoint && status === 404) {
    //       item.consultation.success = true
    //     } else {
    //       item.consultation.success = false
    //     }
    //   } finally {
    //     item.consultation.fetched = true
    //   }
    // },
    updateInvitationStatus(invitation) {
      const findIndex = this.invitations.findIndex((item) => item.id === invitation.id)
      if (findIndex >= 0) {
        this.invitations[findIndex].status = invitation.status
      }
    },
    isDisabled(actionType, item) {
      let disabled;
      switch (actionType) {
        case 'sms':
        case 'whatsapp':
          disabled = !item.lead.phone
          break;

        case 'email':
          disabled = !item.lead.email
          break;

        default:
          break;
      }
      return disabled
    }
  },
}
</script>

<style scoped>
::v-deep svg.v-icon.notranslate.v-data-table-header__icon {
  height: 8px !important;
  width: 8px !important;
  margin-left: 8px;
}
</style>
